/**
 * JobsMoneyCounter.jsx — Admin-only money strip for the Jobs tab.
 *
 * Revenue, crew cost, recurring overhead and "company + Cyprian" profit for a
 * period, attributed by delivery date (fallback: shoot date, then created date).
 *
 * Every figure is an ESTIMATE built from the jobs table alone — jobs.revenue and
 * the crew splits hanging off it. It is not cash accounting and does not read
 * invoices, so it answers "what did the work I booked come to", not "what landed
 * in the bank". Only jobs at 'confirmed' or later count; intake and quoted are
 * pipeline, and voided work never counts.
 * The Month/Day toggle switches granularity: ◀ ▶ step one month or one day at a
 * time, and % chips compare against the previous month/day. The period label
 * expands to per-job include/exclude checkboxes, persisted per-user
 * (profiles.money_excluded_jobs, same pattern as pinning).
 *
 * Profit = revenue − others' crew cost − job-linked expenses − recurring
 *          overhead. Cyprian's own crew pay stays in the profit.
 *
 * CREW COST COUNTS BUDGETED HOURLY WORK AS REAL. Nobody logs time, so the
 * payout ledger — which refuses to pay an estimate — has never written a single
 * hourly row, and each side lead's row silently absorbed the hourly budget
 * instead. Where that lead was Cyprian, the owner filter then erased the cost
 * altogether. lib/jobMoney.js's jobCrewCost takes a per-bucket max of the
 * ledger and the calculator snapshot, which recovers the estimate without
 * double counting the cases a non-owner lead already absorbed.
 *
 * THE MONTH IN PROGRESS IS A PROJECTION, shown whole (decided by Cyprian
 * 2026-09-01). Every booked job dated anywhere in the month counts, against the
 * month's full overhead, so on the 1st the strip already says where the month
 * lands instead of reporting $0 over a month of booked work. The period label
 * carries "projected" for the current and future periods. An earlier
 * month-to-date version cut revenue and overhead at today and compared against
 * the previous month's first N days; it read as an empty month every time it
 * was opened early, which is exactly when the number matters.
 *
 * Known remaining gap: `isOwner` (now in lib/jobMoney.js) matches a hardcoded
 * name string, so renaming Cyprian in the ledger or the roster would silently
 * reclassify his pay as crew cost.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Paper, Typography, IconButton, Tooltip, Collapse, Checkbox, ButtonBase, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../lib/api';
import { isBooked, jobDate, jobCrewCost, localDateStr } from '../../lib/jobMoney';
import { snakeToCamel } from '../../lib/utils';
import { useJobs } from '../../hooks/useJobs';
import { useExpenses, useStaticExpenses } from '../../hooks/useExpenses';
import { useTimeEntries } from '../../hooks/useTimeEntries';
import { useMoneyExcludedJobs } from '../../hooks/useMoneyExcludedJobs';
import { useRBAC } from '../../hooks/useRBAC';

// isBooked / isVoided / jobDate / isOwner / jobCrewCost all live in
// lib/jobMoney.js so the Jobs list headers and this strip cannot drift apart.
// Do not reintroduce local copies.

// ── Period helpers ───────────────────────────────────────────────────────────
// A period key is 'YYYY-MM' (month mode) or 'YYYY-MM-DD' (day mode).

function keyLen(mode) { return mode === 'day' ? 10 : 7; }

function pad(n) { return String(n).padStart(2, '0'); }

// localDateStr / jobDate live in lib/jobMoney.js too — the Jobs list's archived
// month headers use the same rule, so a job cannot file under two months.

function jobPeriodKey(job, mode) {
  const d = jobDate(job);
  return d ? d.slice(0, keyLen(mode)) : null;
}

function currentPeriodKey(mode) {
  const now = new Date();
  const base = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  return mode === 'day' ? `${base}-${pad(now.getDate())}` : base;
}

function shiftPeriod(key, delta, mode) {
  const [y, m, d] = key.split('-').map(Number);
  if (mode === 'day') {
    const dt = new Date(y, m - 1, d + delta);
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  }
  const dt = new Date(y, m - 1 + delta, 1);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}`;
}

function periodLabel(key, mode, style = 'short') {
  const [y, m, d] = key.split('-').map(Number);
  if (mode === 'day') {
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-US',
      style === 'long'
        ? { weekday: 'long', month: 'long', day: 'numeric' }
        : { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: style === 'long' ? 'long' : 'short', year: 'numeric' });
}

/** First instant of the period. */
function periodStart(key, mode) {
  const [y, m, d] = key.split('-').map(Number);
  return mode === 'day' ? new Date(y, m - 1, d, 0, 0, 0) : new Date(y, m - 1, 1, 0, 0, 0);
}

/** Last instant of the period — used to decide whether a recurring expense was live. */
function periodEnd(key, mode) {
  const [y, m, d] = key.split('-').map(Number);
  return mode === 'day'
    ? new Date(y, m - 1, d, 23, 59, 59)
    : new Date(y, m, 0, 23, 59, 59);
}

function daysInMonthOf(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m, 0).getDate();
}

function fmtMoney(n) {
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

function pctVs(cur, prev) {
  if (prev == null || prev <= 0) return null;
  return ((cur - prev) / prev) * 100;
}

function DeltaChip({ pct, upIsGood = true }) {
  // Hide no-change chips (rounds to 0.0%) — in Day mode overhead is flat within
  // a month, so a permanent "▲ 0.0%" would just be noise.
  if (pct == null || Math.abs(pct) < 0.05) return null;
  const up = pct >= 0;
  const good = up === upIsGood;
  return (
    <Box
      component="span"
      sx={{
        px: 0.6,
        py: 0.1,
        borderRadius: 1,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1.4,
        color: good ? 'success.main' : 'error.main',
        bgcolor: good ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)',
        whiteSpace: 'nowrap',
      }}
    >
      {up ? '▲' : '▼'} {Math.abs(pct) >= 100 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1)}%
    </Box>
  );
}

function Stat({ label, value, pct, upIsGood, tooltip, loading = false }) {
  const labelEl = (
    <Typography
      variant="caption"
      sx={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', cursor: tooltip ? 'help' : 'inherit' }}
    >
      {label}
    </Typography>
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      {tooltip ? <Tooltip title={tooltip} arrow>{labelEl}</Tooltip> : labelEl}
      {/* While data is in flight, show nothing rather than $0 — a dropped ledger
          request used to render a confident, plausible, wrong number. */}
      {loading ? (
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.2, color: 'text.disabled' }}>—</Typography>
      ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.2, color: value < 0 ? 'error.main' : 'text.primary' }}>
          {fmtMoney(value)}
        </Typography>
        <DeltaChip pct={pct} upIsGood={upIsGood} />
      </Box>
      )}
    </Box>
  );
}

export function MoneyCounterInner({ exclusions }) {
  const { excludedIds, isExcluded, toggleExcluded } = exclusions;
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();
  const { data: expenses = [] } = useExpenses();
  const { data: staticExpenses = [], isLoading: staticLoading } = useStaticExpenses();
  const { data: timeEntries = [] } = useTimeEntries();
  // Same key as tableToQueryKey's job_earnings_ledger → realtime invalidation.
  const { data: ledgerRaw = [], isLoading: ledgerLoading } = useQuery({
    queryKey: ['job-earnings-ledger'],
    queryFn: () => api.get('/job-earnings-ledger'),
  });
  // The three sources every figure depends on. expenses/timeEntries are not
  // included: both tables are empty today, so waiting on them would mean the
  // strip never leaves its placeholder.
  const isLoading = jobsLoading || staticLoading || ledgerLoading;

  const [mode, setMode] = useState('month'); // 'month' | 'day'
  const [viewKey, setViewKey] = useState(() => currentPeriodKey('month'));
  const [expanded, setExpanded] = useState(false);

  const switchMode = (next) => {
    if (!next || next === mode) return;
    setMode(next);
    setViewKey(currentPeriodKey(next)); // always land on today / this month
  };

  // Per-job cost pieces (independent of exclusions/period).
  //
  // Crew cost is jobCrewCost's per-bucket max(ledger, snapshot), so budgeted
  // hourly crew count as a real expense even though nobody logs time and the
  // payout ledger therefore never writes an hourly row. Iterating JOBS rather
  // than ledger rows matters: a job with no ledger rows at all still has a
  // snapshot, and the old ledger-first loop made it disappear from cost.
  const { crewByJob, expByJob } = useMemo(() => {
    const crew = new Map();
    const exp = new Map();
    const ledgerByJob = new Map();
    const entriesByJob = new Map();
    const push = (map, jobId, v) => {
      if (!jobId) return;
      if (!map.has(jobId)) map.set(jobId, []);
      map.get(jobId).push(v);
    };
    for (const raw of ledgerRaw) {
      const r = snakeToCamel(raw);
      push(ledgerByJob, r.jobId, r);
    }
    for (const t of timeEntries) push(entriesByJob, t.jobId, t);
    for (const j of jobs) {
      const { total } = jobCrewCost(j, ledgerByJob.get(j.id) || [], entriesByJob.get(j.id) || []);
      if (total) crew.set(j.id, total);
    }
    for (const e of expenses) {
      if (e.jobId) exp.set(e.jobId, (exp.get(e.jobId) || 0) + (parseFloat(e.amount) || 0));
    }
    return { crewByJob: crew, expByJob: exp };
  }, [jobs, ledgerRaw, timeEntries, expenses]);

  // Period aggregates from INCLUDED jobs only (checkbox exclusions apply
  // everywhere, so % comparisons stay apples-to-apples). No cut at today: the
  // month in progress is a projection of the whole month (see header).
  const periods = useMemo(() => {
    const map = new Map(); // periodKey → { revenue, crew, profit }
    for (const j of jobs) {
      if (!isBooked(j) || excludedIds.includes(j.id)) continue;
      const key = jobPeriodKey(j, mode);
      if (!key) continue;
      const rev = parseFloat(j.revenue) || 0;
      const crew = crewByJob.get(j.id) || 0;
      if (!map.has(key)) map.set(key, { revenue: 0, crew: 0, profit: 0 });
      const p = map.get(key);
      p.revenue += rev;
      p.crew += crew;
      p.profit += rev - crew - (expByJob.get(j.id) || 0);
    }
    return map;
  }, [jobs, crewByJob, expByJob, excludedIds, mode]);

  // Recurring overhead actually live during the viewed period.
  //
  // Three bounds the first version missed, all of them wrong in the same
  // direction (confident numbers for periods the expense did not exist in):
  //   START  a row costs nothing before it existed. Everything here was seeded
  //          2026-04-02, so without this every earlier month invented $2,303 of
  //          burn and reported it as a loss.
  //   STOP   expires_date is the LAST CHARGE date and usually falls AFTER
  //          cancelled_at (Cox: cancelled Jul 24, expires Jul 27). Stopping at
  //          the cancel date drops charges the cancel dialog explicitly
  //          promises to keep running.
  //   ONCE   a 'one-time' row belongs to its own month, not to every month for
  //          the rest of time.
  const prevKey = shiftPeriod(viewKey, -1, mode);
  const overheadFor = useMemo(() => {
    return (key) => {
      const start = periodStart(key, mode);
      const end = periodEnd(key, mode);
      const monthly = staticExpenses.reduce((sum, se) => {
        // Not born yet.
        if (se.createdAt && new Date(se.createdAt) > end) return sum;
        // Already stopped. expires_date wins when set: it is the real last day.
        const stop = se.expiresDate
          ? new Date(`${String(se.expiresDate).slice(0, 10)}T23:59:59`)
          : (se.cancelledAt ? new Date(se.cancelledAt) : null);
        if (stop && stop < start) return sum;

        const amt = parseFloat(se.amount) || 0;
        const f = se.frequency || 'monthly';
        if (f === 'monthly') return sum + amt;
        if (f === 'annual' || f === 'yearly') return sum + amt / 12;
        if (f === 'quarterly') return sum + amt / 3;
        if (f === 'weekly') return sum + amt * 4.33;
        if (f === 'one-time' || f === 'once') {
          const when = localDateStr(se.chargeDate) || localDateStr(se.createdAt);
          return when && when.slice(0, 7) === key.slice(0, 7) ? sum + amt : sum;
        }
        return sum + amt;
      }, 0);

      return mode === 'day' ? monthly / daysInMonthOf(key) : monthly;
    };
  }, [staticExpenses, mode]);

  // Forward limit: never past the latest period holding jobs, but always at
  // least one step ahead of now so "tomorrow" / "next month" stays reachable.
  // Built from every booked job, not from `periods`, so excluding the furthest
  // future job cannot strand its period out of reach and make the exclusion
  // impossible to undo.
  const maxKey = useMemo(() => {
    let max = shiftPeriod(currentPeriodKey(mode), 1, mode);
    for (const j of jobs) {
      if (!isBooked(j)) continue;
      const k = jobPeriodKey(j, mode);
      if (k && k > max) max = k;
    }
    return max;
  }, [jobs, mode]);

  // Backward limit: the earliest period that has anything real in it. Without a
  // floor the ◀ arrow runs forever into empty months, each reporting overhead
  // for subscriptions that did not exist and a matching invented loss.
  const minKey = useMemo(() => {
    let min = currentPeriodKey(mode);
    for (const j of jobs) {
      if (!isBooked(j)) continue;
      const k = jobPeriodKey(j, mode);
      if (k && k < min) min = k;
    }
    for (const se of staticExpenses) {
      const born = localDateStr(se.createdAt);
      if (born) {
        const k = born.slice(0, keyLen(mode));
        if (k < min) min = k;
      }
    }
    return min;
  }, [jobs, staticExpenses, mode]);

  // All of the viewed period's jobs (excluded ones too, so they can be re-checked).
  const periodJobs = useMemo(
    () =>
      jobs
        .filter((j) => isBooked(j) && jobPeriodKey(j, mode) === viewKey)
        .sort((a, b) => (parseFloat(b.revenue) || 0) - (parseFloat(a.revenue) || 0)),
    [jobs, viewKey, mode],
  );

  // The current and future periods are projections: booked work that has not
  // all landed yet. Past periods are what the work came to.
  const isProjected = viewKey >= currentPeriodKey(mode);

  const cur = periods.get(viewKey) || { revenue: 0, crew: 0, profit: 0 };
  const prev = periods.get(prevKey) || { revenue: 0, crew: 0, profit: 0 };

  const overhead = overheadFor(viewKey);
  const prevOverhead = overheadFor(prevKey);
  const netProfit = cur.profit - overhead;
  // Always a real number now. A month with no work but $1,745 of subscriptions
  // is a loss, and reporting it as "no jobs" hid exactly the transition that
  // matters most: the month the business climbs back out.
  const prevNetProfit = prev.profit - prevOverhead;
  const includedCount = periodJobs.filter((j) => !isExcluded(j.id)).length;
  const unit = mode === 'day' ? 'day' : 'month';

  return (
    <Box sx={{ px: 1.5, pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => switchMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 0.9, py: 0.15, fontSize: '0.6rem', fontWeight: 700,
              letterSpacing: 0.4, textTransform: 'uppercase', border: 0,
            },
          }}
        >
          <ToggleButton value="month">Mo</ToggleButton>
          <ToggleButton value="day">Day</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={() => setViewKey((k) => shiftPeriod(k, -1, mode))}
            disabled={viewKey <= minKey}
            sx={{ p: 0.25 }}
            aria-label={`Previous ${unit}`}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <ButtonBase
            onClick={() => setExpanded((e) => !e)}
            sx={{ borderRadius: 1, px: 0.5, display: 'flex', flexDirection: 'column', minWidth: mode === 'day' ? 100 : 86 }}
            aria-label={`Toggle ${unit} job list`}
          >
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
              {periodLabel(viewKey, mode)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {includedCount === periodJobs.length
                ? `${periodJobs.length} ${periodJobs.length === 1 ? 'job' : 'jobs'}`
                : `${includedCount}/${periodJobs.length} jobs`}
              {isProjected ? ' · projected' : ''}
              <ExpandMoreIcon sx={{ fontSize: 12, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
            </Typography>
          </ButtonBase>
          <IconButton
            size="small"
            onClick={() => setViewKey((k) => shiftPeriod(k, 1, mode))}
            disabled={viewKey >= maxKey}
            sx={{ p: 0.25 }}
            aria-label={`Next ${unit}`}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stat
          loading={isLoading}
          label="Revenue"
          value={cur.revenue}
          pct={pctVs(cur.revenue, prev.revenue)}
          tooltip={`Booked work (confirmed or later), by delivery date${isProjected ? '. Projected: every job dated in this period counts, landed or not' : ''}`}
        />
        <Stat
          loading={isLoading}
          label="Crew"
          value={cur.crew}
          pct={pctVs(cur.crew, prev.crew)}
          upIsGood={false}
          tooltip="Pay splits, budgeted hourly crew and mileage, for everyone except Cyprian. Hourly crew who have not logged time count at their estimate."
        />
        <Stat
          loading={isLoading}
          label="Overhead"
          value={overhead}
          pct={pctVs(overhead, prevOverhead)}
          upIsGood={false}
          tooltip={mode === 'day'
            ? 'Recurring subscriptions and leases that were live that day, prorated (monthly total ÷ days in month)'
            : 'Recurring subscriptions and leases live this month, charged for the full month'}
        />
        <Stat
          loading={isLoading}
          label="Profit"
          value={netProfit}
          pct={pctVs(netProfit, prevNetProfit)}
          tooltip={`Company + Cyprian: revenue − crew pay to others − their mileage − job expenses − recurring overhead${mode === 'day' ? ' (prorated)' : isProjected ? ' (projected)' : ''}`}
        />

        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.66rem', textAlign: 'right' }}>
          {periodLabel(prevKey, mode)} · {fmtMoney(prev.revenue)} rev · {fmtMoney(prevNetProfit)} profit
        </Typography>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', maxHeight: 200, overflowY: 'auto' }}>
          {periodJobs.length === 0 && (
            <Typography variant="caption" color="text.secondary">No jobs in {periodLabel(viewKey, mode, 'long')}</Typography>
          )}
          {periodJobs.map((j) => {
            const included = !isExcluded(j.id);
            return (
              <Box
                key={j.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.1, opacity: included ? 1 : 0.5 }}
              >
                <Checkbox
                  size="small"
                  checked={included}
                  onChange={() => toggleExcluded(j.id)}
                  sx={{ p: 0.4 }}
                  inputProps={{ 'aria-label': `Include ${j.title || j.clientName || j.id} in totals` }}
                />
                <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem' }}>
                  {j.title || j.clientName || j.id}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', flexShrink: 0 }}>
                  {fmtMoney(parseFloat(j.revenue) || 0)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function JobsMoneyCounter() {
  // No settings passed → matrix fallback: admins (admin/super_admin) only,
  // same gate as JobDetail's ledger panel.
  const { hasAccess } = useRBAC();
  const exclusions = useMoneyExcludedJobs();
  // Shut on every load, desktop and mobile alike. Revenue is something you go
  // and look at, not something that should be on screen (and over your
  // shoulder) every time the Jobs tab opens. Deliberately not persisted:
  // "collapsed by default" means every visit, not just the first one.
  // unmountOnExit means the ledger query never runs while it is closed.
  const [open, setOpen] = useState(false);
  if (!hasAccess('payroll', 'full')) return null;
  return (
    <Paper>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="jobs-money-panel"
        aria-label={open ? 'Hide money summary' : 'Show money summary'}
        sx={{
          width: '100%',
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}
        >
          Money
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            ml: 'auto',
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit id="jobs-money-panel">
        <MoneyCounterInner exclusions={exclusions} />
      </Collapse>
    </Paper>
  );
}
